<template>
  <div class="changelog-item" :class="{ 'changelog-item__detail': isDetail }">
    <button
      v-if="isDetail && onBack"
      class="changelog-item__back"
      @click="onBack"
    >
      <span class="back-link__icon iconfont iback"></span>
      <span class="back-link__text changelog-item__back-text">Back</span>
    </button>
    <div class="changelog-item__header">
      <p class="changelog-item__date">
        {{ formattedDate }}
      </p>
      <p
        v-if="!item.is_historical && !item.is_read && !isDetail"
        class="changelog-item__read"
      >
        Unread
      </p>
    </div>
    <div class="changelog-item__content">
      <h4 class="changelog-item__title">{{ item.title }}</h4>
      <div
        class="changelog-item__text"
        :class="{ 'changelog-item__text--cropped': !isDetail }"
        @click="handleLinkClick"
        v-html="isDetail ? item.text : item.blurb"
      ></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Changelog } from '@/models/interfaces'
import * as Mixpanel from '@/analytics/mixpanel/mixpanel'

const props = defineProps<{
  item: Changelog
  isDetail?: boolean
  onBack?: () => void
}>()

const handleLinkClick = (event: MouseEvent): void => {
  const target = event.target as HTMLElement
  if (target.tagName === 'A') {
    event.preventDefault()
    const href = target.getAttribute('href')
    if (href) {
      Mixpanel.trackChangelogLinkClick(href)
      window.open(href, '_blank', 'noopener,noreferrer')
    }
  }
}

const formattedDate = computed(() => {
  return new Date(props.item.change_date).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
})
</script>

<style lang="scss" scoped>
.changelog {
  &-item {
    padding: 40px 0px;
    margin: 0 120px;
    border-bottom: 1px solid $c-black-13;
    cursor: pointer;
    margin-inline: auto;
    max-width: 900px;

    &:hover {
      .changelog-item__title {
        color: $c-black-2;
        transition: color 0.3s ease;
      }
    }

    @media only screen and (max-width: $w-tablet-s) {
      padding: 40px 0px;
      margin: 0px;
    }

    &__detail {
      cursor: default;
      .changelog-item__title {
        color: $c-black-2 !important;
      }
    }

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }

    &__header {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      gap: 8px;
      min-height: 22px;
    }

    &__date {
      color: $c-black-6;
      font-size: 14px;
      line-height: 16px;
      margin-bottom: 0px;
    }

    &__title {
      color: $c-black-10;
      font-size: 24px;
      line-height: 32px;
      margin-bottom: 12px;
      font-weight: 500;
    }

    &__text {
      color: $c-black-15;
      font-size: 16px;
      line-height: 22px;
      letter-spacing: -0.32px;
      font-weight: 400;
      max-width: 900px;

      :deep(img) {
        margin-block: 8px;
        max-width: 600px;
        height: auto;

        @media only screen and (max-width: $w-tablet-s) {
          max-width: min(100%, 600px);
        }
      }

      :deep(a) {
        color: $c-blue-2;
        text-decoration: underline;
      }

      :deep(ul) {
        margin-block: 8px;
        padding-inline-start: 20px;
      }
      :deep(ul li) {
        list-style-type: disc;
      }
    }

    &__text--cropped {
      display: -webkit-box;
      line-clamp: 3;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;

      @media only screen and (max-width: $w-mobile-s) {
        font-size: 14px;
        line-height: 21px;
        letter-spacing: -0.28px;
      }
    }

    &__read {
      font-size: 12px;
      color: $c-blue-2;
      font-weight: 500;

      &::before {
        color: $c-black-4;
        content: '• ';
        margin-right: 6px;
      }
    }

    &__details {
      min-height: 60vh;
    }
    &__back {
      margin-bottom: 20px;
      display: flex;
      align-items: center;
      gap: 6px;

      @media only screen and (max-width: $w-tablet-s) {
        margin-bottom: 12px;
      }

      &-text {
        font-size: 14px;
        line-height: 16px;
      }
    }
  }
}

.night-mode {
  .changelog-item {
    border-color: $c-dark-3;

    &:hover {
      .changelog-item__title {
        color: $c-black-11;
        transition: color 0.3s ease;
      }
    }

    &__detail {
      .changelog-item__title {
        color: $c-black-11 !important;
      }
    }

    &__date {
      color: $c-black-3;
    }

    &__title {
      color: $c-black-6;
    }

    &__text,
    &__text :deep(*) {
      color: $c-black-6;
    }

    &__read {
      color: $c-blue-3;
    }

    &__back {
      color: $c-black-6;
    }
  }
}
</style>
